"use client"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { ComponentConfig } from "@measured/puck"
import { Modal } from "antd"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import useIsMobile from "../../../lib/use-is-mobile"
import "./media.css"

export interface MediaItems {
  item: MediaProps[]
}

export interface MediaProps {
  country?: string
  city?: string
  coverImage?: string
  previewImage?: { previewImage: string }[]
}

export const RenderCustomMedia: ComponentConfig<MediaItems> = {
  render: ({ item }) => {
    const [selectedMedia, setSelectedMedia] = useState<MediaProps | null>(null)
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [isAnimating, setIsAnimating] = useState(false)

    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const sliderRef = useRef<HTMLDivElement | null>(null)

    const slidesToShow = 4
    const totalSlides = item.length
    const maxIndex = Math.max(0, totalSlides - slidesToShow)

    const handlePrev = () => {
      if (totalSlides > slidesToShow && !isAnimating) {
        setIsAnimating(true)
        setCurrentIndex(prevIndex => {
          const newIndex = prevIndex - slidesToShow
          return Math.max(0, newIndex)
        })

        setTimeout(() => {
          setIsAnimating(false)
        }, 500)
      }
    }

    const handleNext = () => {
      if (totalSlides > slidesToShow && !isAnimating) {
        setIsAnimating(true)
        setCurrentIndex(prevIndex => {
          const newIndex = prevIndex + slidesToShow
          return Math.min(maxIndex, newIndex)
        })

        setTimeout(() => {
          setIsAnimating(false)
        }, 500)
      }
    }

    const handleItemClick = (media: MediaProps) => {
      setSelectedMedia(media)
      setCurrentImageIndex(0)
      setIsModalOpen(true)
    }

    const handleThumbnailClick = (index: number) => {
      setCurrentImageIndex(index)
    }

    const handleModalClose = () => {
      setIsModalOpen(false)
      setSelectedMedia(null)
      setCurrentImageIndex(0)
    }

    const getAllImages = (item: MediaProps): string[] => {
      const previewImages = item.previewImage ? item.previewImage.map(img => img.previewImage) : []

      return [...(item.coverImage ? [item.coverImage] : []), ...previewImages]
    }

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true)
      setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = "auto"
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = "smooth"
      }
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !scrollContainerRef.current) return
      const x = e.pageX - scrollContainerRef.current.offsetLeft
      const walk = (x - startX) * 1.5
      scrollContainerRef.current.scrollLeft -= walk
      setStartX(x)
    }

    const handleMouseLeave = () => {
      if (isDragging) {
        setIsDragging(false)
        if (scrollContainerRef.current) {
          scrollContainerRef.current.style.scrollBehavior = "smooth"
        }
      }
    }

    useEffect(() => {
      if (!scrollContainerRef.current || !selectedMedia) return

      const container = scrollContainerRef.current
      const thumbnails = container.querySelectorAll("button")
      const activeThumbnail = thumbnails[currentImageIndex]

      if (activeThumbnail) {
        const containerWidth = container.clientWidth
        const thumbnailWidth = activeThumbnail.clientWidth
        const thumbnailOffsetLeft = activeThumbnail.offsetLeft

        const scrollPosition = thumbnailOffsetLeft - containerWidth / 2 + thumbnailWidth / 2

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        })
      }
    }, [currentImageIndex])

    const isMobile = useIsMobile()

    return (
      <>
        <div className="py-[65px] px-4">
          <h2 className="text-[64px] leading-[72px] font-bold text-center">Media</h2>
          <div className="w-full mt-[67px]">
            <div className="relative overflow-hidden">
              <div
                ref={sliderRef}
                className="flex flex-col gap-10 sm:gap-0 sm:flex-row w-full sm:h-[540px] group/slider"
                style={
                  !isMobile
                    ? {
                        transform: `translateX(-${(currentIndex / item.length) * 100}%)`,
                        width: `${(item.length / slidesToShow) * 100}%`,
                        transition: "transform 0.5s cubic-bezier(0.33, 1, 0.68, 1)",
                      }
                    : {}
                }
              >
                {item.map((slide, index) => (
                  <div
                    onClick={() => handleItemClick(slide)}
                    key={index}
                    className="sm:mx-2.5 sm:w-1/4 relative sm:group/slide flex flex-col gap-6 sm:group-hover/slider:opacity-50 hover:!opacity-100 hover:cursor-pointer"
                    style={{
                      transition: "all 0.4s cubic-bezier(0.33, 1, 0.68, 1)",
                    }}
                  >
                    <div
                      className="overflow-hidden rounded-lg shadow-lg w-full h-[462px] transition-all duration-300 ease-in-out"
                      style={{
                        borderBottom: "0px solid #0B9444",
                        transition: "all 0.4s cubic-bezier(0.33, 1, 0.68, 1)",
                      }}
                    >
                      <img
                        src={`${ADMIN_API_CONFIG.API_URL}${slide.coverImage}`}
                        alt={`${slide.city}, ${slide.country}`}
                        className="w-full h-full object-cover object-center transition-transform duration-300"
                        style={{
                          transform: "scale(1)",
                          transition: "transform 0.4s cubic-bezier(0.33, 1, 0.68, 1)",
                        }}
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="font-bold text-[#242F3E] text-2xl leading-7">{slide.country}</h3>
                      <div className="flex flex-row items-center justify-center gap-3">
                        <div className="h-[1px] w-[68px] bg-gray-300 rounded-full shadow-md opacity-50"></div>
                        <p className="text-sm text-gray-500">{slide.city}</p>
                        <div className="h-[1px] w-[68px] bg-gray-300 rounded-full shadow-md opacity-50"></div>
                      </div>
                    </div>
                    <style jsx>{`
                      .sm\\:group\\/slide:hover {
                        width: 33.33% !important;
                        transition: all 0.4s cubic-bezier(0.33, 1, 0.68, 1);
                      }
                      .sm\\:group\\/slide:hover .overflow-hidden {
                        border-bottom: 6px solid #0b9444;
                        transition: all 0.4s cubic-bezier(0.33, 1, 0.68, 1);
                      }
                      .sm\\:group\\/slide:hover img {
                        transform: scale(1.05);
                        transition: transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
                      }
                    `}</style>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-[30px]">
              <div className="justify-center gap-2 lg:flex hidden">
                <button
                  className={`flex p-1 rounded-full w-10 h-10 items-center justify-center transition-all duration-300 ${
                    currentIndex === 0 ? "opacity-50" : "hover:bg-[#D6D9DE]"
                  }`}
                  style={{ backgroundColor: "#EBEDF0" }}
                  onClick={handlePrev}
                  disabled={currentIndex === 0 || isAnimating}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#000000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <button
                  className={`flex p-1 rounded-full w-10 h-10 items-center justify-center transition-all duration-300 ${
                    currentIndex >= maxIndex ? "opacity-50" : "hover:bg-[#D6D9DE]"
                  }`}
                  style={{ backgroundColor: "#EBEDF0" }}
                  onClick={handleNext}
                  disabled={currentIndex >= maxIndex || isAnimating}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#000000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <Modal
            open={isModalOpen}
            onCancel={handleModalClose}
            footer={null}
            width={"100vw"}
            centered
            closable={false}
            className="fullscreen-modal z-20"
            styles={{
              content: {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                padding: 0,
                margin: 0,
                zIndex: 20,
              },
              mask: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                zIndex: 20,
              },
              body: {
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 20,
              },
            }}
          >
            {selectedMedia && (
              <div className="relative h-screen w-screen bg-black overflow-hidden z-20">
                <button
                  onClick={handleModalClose}
                  className="absolute top-[10%] sm:top-[195px] left-4 sm:left-[120px] p-2 bg-[#FFFFFF80] text-white rounded-full z-20 backdrop-blur-[4px] w-10 h-10 transition-transform duration-300 hover:scale-110"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left w-full h-full"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>

                <div className="relative h-full w-full flex items-center justify-center">
                  <img
                    src={`${ADMIN_API_CONFIG.API_URL}${getAllImages(selectedMedia)[currentImageIndex]}`}
                    alt={`${selectedMedia.country}`}
                    className="h-screen w-screen object-cover transition-all duration-500 transform"
                    style={{
                      transition: "opacity 0.4s ease-in-out",
                    }}
                  />
                  <div className="absolute top-[15%] sm:top-[283px] right-6 sm:right-[120px] text-white text-right flex flex-col gap-1 rounded-lg">
                    <h2 className="text-[64px] leading-[72px] font-bold drop-shadow-lg sm:text-shadow-md">
                      {selectedMedia.country}
                    </h2>
                    <div className="gap-4 flex flex-row items-center justify-center w-full">
                      <div className="h-px w-full bg-white"></div>
                      <p className="text-2xl leading-[24px] drop-shadow-lg sm:text-shadow-md">{selectedMedia.city}</p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute bottom-0 left-0 right-0 sm:px-[120px]"
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    background: "linear-gradient(to bottom, #00000000 0%, #000000 100%)",
                  }}
                >
                  <div className="flex flex-row gap-1 justify-center">
                    {getAllImages(selectedMedia).map((image, index) => (
                      <div
                        className={`w-10 h-1 rounded-full transform transition-all duration-300 ease-out cursor-pointer ${
                          currentImageIndex === index ? "bg-white opacity-100 scale-y-150" : "bg-white opacity-50"
                        }`}
                        key={index}
                        onClick={() => handleThumbnailClick(index)}
                      />
                    ))}
                  </div>
                  <div
                    className="flex pl-6 sm:justify-center gap-6 sm:px-[120px] py-6 overflow-x-auto custom-scrollbar-hidden"
                    ref={scrollContainerRef}
                    style={{ display: "flex", whiteSpace: "nowrap", cursor: isDragging ? "grabbing" : "grab" }}
                  >
                    {getAllImages(selectedMedia).map((image, index) => (
                      <button
                        key={index}
                        onClick={() => handleThumbnailClick(index)}
                        className={`relative h-[100px] w-[140px] overflow-hidden rounded-lg transition-all duration-300 ${
                          currentImageIndex === index
                            ? "border-2 border-white rounded-2xl scale-105"
                            : "border border-transparent"
                        }`}
                        style={{ flexShrink: 0 }}
                      >
                        <img
                          src={`${ADMIN_API_CONFIG.API_URL}${image}`}
                          alt={`Preview ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {currentImageIndex !== index && <div className="absolute inset-0 bg-black/30" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </>
    )
  },
}
